import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore, useNotificationStore } from '../stores';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export default function useSocket() {
  const socketRef = useRef(null);
  const { isAuthenticated } = useAuthStore();
  const { addNotification } = useNotificationStore();
  
  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }
    
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    
    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
    
    socketRef.current.on('connect', () => {
      console.log('Socket connected');
    });
    
    socketRef.current.on('disconnect', () => {
      console.log('Socket disconnected');
    });
    
    socketRef.current.on('notification', (notification) => {
      addNotification(notification);
    });
    
    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, addNotification]);
  
  const joinProject = useCallback((projectId) => {
    if (socketRef.current) {
      socketRef.current.emit('join:project', projectId);
    }
  }, []);
  
  const leaveProject = useCallback((projectId) => {
    if (socketRef.current) {
      socketRef.current.emit('leave:project', projectId);
    }
  }, []);
  
  const sendMessage = useCallback((projectId, content, mentions = []) => {
    if (socketRef.current) {
      socketRef.current.emit('chat:message', { projectId, content, mentions });
    }
  }, []);
  
  const startTyping = useCallback((projectId) => {
    if (socketRef.current) {
      socketRef.current.emit('chat:typing', { projectId });
    }
  }, []);
  
  const stopTyping = useCallback((projectId) => {
    if (socketRef.current) {
      socketRef.current.emit('chat:stop-typing', { projectId });
    }
  }, []);
  
  const onMessage = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:message', callback);
      return () => socketRef.current?.off('chat:message', callback);
    }
    return () => {};
  }, []);
  
  const onTyping = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:typing', callback);
      return () => socketRef.current?.off('chat:typing', callback);
    }
    return () => {};
  }, []);
  
  const onStopTyping = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:stop-typing', callback);
      return () => socketRef.current?.off('chat:stop-typing', callback);
    }
    return () => {};
  }, []);
  
  const onError = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:error', callback);
      return () => socketRef.current?.off('chat:error', callback);
    }
    return () => {};
  }, []);
  
  return {
    socket: socketRef.current,
    joinProject,
    leaveProject,
    sendMessage,
    startTyping,
    stopTyping,
    onMessage,
    onTyping,
    onStopTyping,
    onError
  };
}
